import * as React from 'react'
import * as Redux from 'react-redux'

import styled from 'styled-components'

import { format, parse } from '@owl-nest/date'
import { t } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import { text } from '@owl-nest/utils'
import * as plume from '@ulule/owl-kit-components/next'

import * as projectModel from '../../../../../models/project'
import { RootState } from '../../../../../../reducers'
import * as modalReducer from '../../../../../../reducers/modals/rewardModal'
import { useReward } from '../../../hooks/useReward'

import { DonationBlock } from '../../../components/DonationBlock'

import { TRACKING_SOURCE } from '../../../utils/trackerEnums'
import * as UFE from '../../../../../../UFE'
import { ShippingInfo } from './ShippingInfo'
import { BaseReward } from '../../../components/Rewards/BaseReward'

type RewardsProps = unknown

export function RewardList(): React.ReactElement<RewardsProps> {
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const modal = Redux.useSelector<RootState, modalReducer.State>((state) => state.application.rewardModal)
  const { handleClick } = useReward()

  const { rewards } = project

  const projectHasEnded = models.project.isFinished<projectModel.ExtraFields>(project)
  const projectIsDonationBased = models.project.isDonationBased(project)

  const deliveryDate = project.shippings.date_delivery
  const deliveryHelperUrl = models.i18n.get(UFE.LINKS.deliveryHelper)

  const featuredReward = getFeaturedReward()
  const sharedRewardId = modal.rewardId

  const availableRewards = React.useMemo(
    () =>
      rewards
        ?.filter((reward) => {
          if (!reward.available) {
            return false
          }

          if (reward.is_featured) {
            return false
          }

          if (reward.is_hidden) {
            return reward.id === Number(sharedRewardId)
          }

          return !reward.is_hidden
        })
        .sort((a, b) => {
          if (a.position && b.position) {
            return a.position - b.position
          }

          return (a.price ?? 0) - (b.price ?? 0)
        }),
    [rewards],
  )

  const soldOutRewards = rewards?.filter((reward) => {
    return !reward.available && !reward.is_hidden
  })

  return (
    <>
      {featuredReward && !projectIsDonationBased && (
        <FeaturedRewardWrapper>
          <BaseReward
            onClick={(id, optionValuesIds) =>
              handleClick(id, {
                optionValuesIds,
                trackingExtraData: { source: TRACKING_SOURCE.PROJECT },
              })
            }
            reward={featuredReward}
            trackView
            disabled={!models.project.isOnline(project)}
          />
          <plume.styles.heading.XXXXS>{t('All available rewards')}</plume.styles.heading.XXXXS>
        </FeaturedRewardWrapper>
      )}

      <RewardListWrapper>
        {!projectIsDonationBased &&
          availableRewards?.map((rawReward, index) => {
            const reward = models.reward.Reward<projectModel.ExtraFields>(rawReward, project)

            return (
              <BaseReward
                disabled={!models.project.isOnline(project)}
                key={index}
                onClick={(id, optionValuesIds) =>
                  handleClick(id, {
                    optionValuesIds,
                    trackingExtraData: { source: TRACKING_SOURCE.PROJECT },
                  })
                }
                reward={reward}
                trackView
              />
            )
          })}

        {!projectHasEnded && <DonationBlock context="project-sidebar" project={project} />}

        {!projectIsDonationBased &&
          soldOutRewards?.map((rawReward, index) => {
            const reward = models.reward.Reward<projectModel.ExtraFields>(rawReward, project)

            return (
              <BaseReward
                disabled={!models.project.isOnline(project)}
                key={index}
                onClick={(id, optionValuesIds) =>
                  handleClick(id, {
                    optionValuesIds,
                    trackingExtraData: { source: TRACKING_SOURCE.PROJECT },
                  })
                }
                reward={reward}
                trackView
              />
            )
          })}
      </RewardListWrapper>

      {!projectIsDonationBased && (
        <ShippingDetailsWrapper>
          {deliveryDate && (
            <>
              <ShippingDetails>
                <ShippingDetailsTitle>
                  <plume.glyphs.stroke.Calendar size={20} />
                  <ETA>{t('Estimated delivery date')}</ETA>
                </ShippingDetailsTitle>

                <plume.styles.copy.S.regular>
                  {t('As of %(date)s', {
                    date: text.capitalize(format(parse(deliveryDate, 'yyyy-MM', new Date()), 'MMMM yyyy')),
                  })}
                </plume.styles.copy.S.regular>
              </ShippingDetails>
              <ShippingInfo project={project} />
            </>
          )}

          <LearnMore>
            <plume.Link href={deliveryHelperUrl} kind="secondary">
              {t('Learn more about rewards and delivery')}
            </plume.Link>
          </LearnMore>
        </ShippingDetailsWrapper>
      )}
    </>
  )

  function getFeaturedReward(): models.reward.Reward<projectModel.ExtraFields> | undefined {
    const featured = rewards?.find((reward) => {
      return reward.is_featured && reward.available
    })

    if (featured !== undefined) {
      return models.reward.Reward(featured, project)
    }
  }
}

const ETA = styled(plume.styles.heading.XXXXXXS)`
  color: ${plume.COLORS.TEXT_SECONDARY};
  margin-left: 10px;
  text-transform: uppercase;
`

const LearnMore = styled(plume.styles.copy.S.regular)``

const RewardListWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 16px;
`

const FeaturedRewardWrapper = styled.div`
  ${plume.styles.heading.XXXXS} {
    margin: 32px 0 20px;
    display: flex;
    justify-content: center;
  }
`

const ShippingDetails = styled.div`
  margin-top: 26px;

  ${plume.styles.heading.XXXS}, ${plume.styles.copy.S.regular} {
    padding-left: 30px;
  }

  ${plume.styles.copy.XS.regular} {
    margin-top: 15px;
    padding-left: 30px;
  }
`

const ShippingDetailsTitle = styled.div`
  align-items: center;
  display: flex;

  svg {
    color: ${plume.COLORS.PRIMARY_SAND_500};
  }
`

const ShippingDetailsWrapper = styled.div`
  ${LearnMore} {
    margin-top: 20px;
  }
`
